<template>
  <v-container class="ma-auto themes__container <xs:(wd-relative wd-z-regionListContainer)" fluid>
    <category-info-sheet-mobile
      v-if="isMobile"
      :region="region"
      :category="category"
      :subcategory-id="subcategoryId"
      :has-geolocation="hasGeolocation"
      :day-mode="category.day_mode"
      :filters="filterOptions"
      :total-count="totalCount"
      :is-branch-card-mode="isBranchCardMode"
      @category-change="handleSubcategorySelected"
      @update-condition="onConditionUpdate"
    />
    <category-info-sheet
      v-else
      :region="region"
      :category="category"
      :subcategory-id="subcategoryId"
      :has-geolocation="hasGeolocation"
      :day-mode="category.day_mode"
      :filters="filterOptions"
      :total-count="totalCount"
      :is-branch-card-mode="isBranchCardMode"
      @category-change="handleSubcategorySelected"
      @update-condition="onConditionUpdate"
    />

    <div class="categories__wrapper">
      <!-- 商品列表 -->
      <v-sheet v-if="isBranchCardMode ? branchesInfo.branches.length > 0 : categoryProductInfo.products.length > 0" class="categories-list" rounded>
        <div class="categories-list__wrapper">
          <v-row v-if="loadingList" class="product-list">
            <v-col v-for="number in 20" :key="number" class="product-list__col" cols="12" md="3" sm="6">
              <div class="product__box">
                <v-skeleton-loader class="mx-auto" max-width="300" type="image, list-item-three-line" />
              </div>
            </v-col>
          </v-row>

          <v-row v-else class="product-list">
            <template v-if="isBranchCardMode">
              <v-col v-for="branch in branchesInfo.branches" :key="branch.bid" class="product-list__col" cols="12" md="3" sm="6">
                <div class="product__box">
                  <BranchCard :branch="branch" :bring-query="false" />
                </div>
              </v-col>
            </template>

            <template v-else>
              <v-col v-for="product in categoryProductInfo.products" :key="product.product_id" class="product-list__col" cols="12" md="3" sm="6">
                <div class="product__box">
                  <product-card :product="product" :bring-query="false" />
                </div>
              </v-col>
            </template>
          </v-row>

          <loading v-if="loadingList" class="list-loading" :width="72" :height="72" color="orange" />
        </div>

        <pagination
          class="mb-9"
          :page="pagingState.page"
          :visible="8"
          :total-count="totalCount"
          :size="pagingState.size"
          @update="updatePagingPage"
        />
      </v-sheet>
      <div v-else class="categories-list--empty">
        <p class="empty-text">{{ $t('filter_empty_message') }}</p>
      </div>
    </div>

    <v-sheet class="serp wd-mt-6 wd-px-5 wd-py-4">
      <h2 class="wd-mb-5 text-h6">{{ $t('all_faq') }}</h2>
      <QAndA :faqs="categoryFaqs" />
    </v-sheet>

    <JsonLdFaq :faq-items="categoryFaqs" />
  </v-container>
</template>

<script setup>
import { useABTestingStore } from '@/store-pinia/abtesting'
import utils from '@/utils'
import notificationComposition from '@/composition/notification'
import { usePageView } from '@/composition/usePageView'
import { provideGtmSource } from '@/composition/useGtmSource'
import { useBreakpoints } from '@/composition/useBreakpoints'
import { GtmEventMap, GtmSource } from '@/view-models/gtm'
import { CategoryOnboard, CategoryType } from '@/view-models/category'
import { ASYNC_DATA_KEYS, getAsyncDataKey } from '@/constants/async-data-keys'

import { getCategoryDefaultFiltersFromQuery, getCategorySelectedFilterTagMapFromQueryString } from '@/utils/filter'

import Pagination from '@/components/pagination/pagination'
import Loading from '@/components/icons/loading'
import PromoCampaignModal from '@/components/dialog/PromoCampaignModal.vue'
import { useGlobalStore } from '@/store-pinia/global'
import CategoryInfoSheet from '@/components/page/categories/category-info-sheet.vue'
import CategoryInfoSheetMobile from '@/components/page/categories/mobile/category-info-sheet--mobile.vue'
import { useRegionSearchNavigation } from '@/composables/useRegionSearchNavigation'
import { useRouteQueryFilterSync } from '@/composables/useRouteQueryFilterSync'

const ProductCard = defineAsyncComponent(() => import('@/components/card/product.vue'))
const BranchCard = defineAsyncComponent(() => import('@/components/card/BranchCard.vue'))
const QAndA = defineAsyncComponent(() => import('@/components/pageCategory/QAndA.vue'))
const JsonLdFaq = defineAsyncComponent(() => import('@/components/jsonld/faq.vue'))

const nuxtApp = useNuxtApp()
const { $store: store, $pinia } = nuxtApp
const config = useRuntimeConfig()

const loadingList = ref(false)
const filterOptions = ref({})

const i18n = useI18n()
const route = useRoute()
const router = useRouter()

const aBTestingStore = useABTestingStore($pinia)
const globalStore = useGlobalStore()
const { isMobile } = useBreakpoints()

const paginationParamOfList = computed(() => ({
  start: (pagingState.page - 1) * pagingState.size,
  size: pagingState.size,
}))
const region = computed(() => store.getters['region/region'])
const category = computed(() => store.getters['category/category'])
const categoryFaqs = computed(() => store.getters['category/categoryFaqs'])
const categoryProductInfo = computed(() => store.getters['product/categoryProductInfo'])
const branchesInfo = computed(() => store.getters['branch/branchesInfo'])
const searchKeyWord = computed(() => store.getters['search/searchKeyword'])
const selectedFilters = computed(() => store.getters['filter/filters'])
const sortType = computed(() => store.getters['filter/sortType'])

const loggedInUser = computed(() => nuxtApp.$auth.user)
const isBranchCardMode = computed(() => aBTestingStore.cardMode === 'branch')
const isSearchKeywordEmpty = computed(() => !searchKeyWord.value)
const totalCount = computed(() => (isBranchCardMode.value ? branchesInfo.value.totalCount : categoryProductInfo.value.totalCount))

const isUrlSubcategoryPattern = computed(() => !!route.query.source_category_id)
const regionID = computed(() => Number(route.params.rid || 0))
const firstLevelCategoryId = computed(() => (isUrlSubcategoryPattern.value ? Number(route.query.source_category_id) : Number(route.params.id)))
const subcategoryId = computed(() => (isUrlSubcategoryPattern.value ? Number(route.params.id) : 0))
const { pushGiftExpiringNotification } = notificationComposition()

useHead(() => {
  const categoryTitle = category.value?.page_title ?? ''
  const titleSuffix = i18n.t('funnow_title')
  const pageTitle = categoryTitle ? `${categoryTitle} | ${titleSuffix}` : `${titleSuffix}`
  const ogImage = utils.CDN + utils.imagePath(category.value?.image)
  const canonicalUrl = `${config.public.BASE_URL}${nuxtApp.$localePath({
    name: 'regions-rid-categories-id',
    params: { rid: route.params.rid, id: route.params.id },
  })}`
  return {
    title: pageTitle,
    meta: [
      { hid: 'title', name: 'title', content: pageTitle },
      { hid: 'description', name: 'description', content: category.value?.meta_desc },
      { hid: 'keywords', name: 'keywords', content: category.value?.meta_keywords },
      { hid: 'og:title', property: 'og:title', content: pageTitle },
      { hid: 'og:description', property: 'og:description', content: category.value?.meta_dec },
      { hid: 'og:image', property: 'og:image', content: ogImage },
    ],
    link: [{ hid: 'i18n-can', rel: 'canonical', href: canonicalUrl }],
  }
})

usePageView({ event: isUrlSubcategoryPattern.value ? GtmEventMap.SUB_CATEGORY_VIEWED : GtmEventMap.CATEGORY_VIEWED })
provideGtmSource(GtmSource.CATEGORY_LIST)
const { pagingState, userPosition, hasGeolocation } = useRegionSearchNavigation({
  fetchList: getProductList,
})

const { sendGTMViewItemList } = useGTMViewItemList()

onMounted(() => {
  const promoCampaignId = route.query.campaign_id ? Number(route.query.campaign_id) : 0

  if (promoCampaignId) {
    globalStore.setRootDynamicComponentOption({ promoCampaignId })
    globalStore.setRootDynamicComponent(PromoCampaignModal)
    globalStore.setShouldRootDynamicComponentShow(true)
  }

  setNotification()
  sendGTMViewItemList()
})

onUnmounted(() => {
  store.dispatch('notification/resetNotification')
})

await useAsyncData(getAsyncDataKey(ASYNC_DATA_KEYS.THEME_PAGE_DATA, [regionID.value, String(route.params.id)]), async () => {
  const regionData = region.value
  const query = route.query
  const isDataFromSubcategory = isUrlSubcategoryPattern.value && !query.filter_keyword
  try {
    if (!regionData || regionData.id !== regionID.value) {
      await store.dispatch('region/getRegion', regionID.value)
    }

    await Promise.all([
      store.dispatch('category/getCategory', { categoryID: firstLevelCategoryId.value }),
      store.dispatch('category/getCategoryFaqs', { regionID: regionID.value, categoryID: firstLevelCategoryId.value }),
    ])

    const categoryInfo = store.state.category.category || {}

    store.dispatch('category/setFirstLevelCategory', categoryInfo)
    if (categoryInfo.web_onboard === CategoryOnboard.Hidden) {
      return nuxtApp.runWithContext(() => navigateTo('/'))
    }

    if (isDataFromSubcategory) {
      await store.dispatch('category/getCategory', { categoryID: subcategoryId.value, isFromSubcategory: true })
      await store.dispatch('category/getCategoryFaqs', { regionID: regionID.value, categoryID: subcategoryId.value })
    }

    const filtersOptionsRes = await fetchFilterOptions({
      categoryID: firstLevelCategoryId.value,
      regionID: regionID.value,
      ...(isDataFromSubcategory && { sub_category_id: subcategoryId.value }),
    })

    const selectedFilterTagMap = getCategorySelectedFilterTagMapFromQueryString(query.filter_tags, filtersOptionsRes.filter_tags)
    const filtersFromRouterQuery = getCategoryDefaultFiltersFromQuery(query, filtersOptionsRes, hasGeolocation.value)

    store.dispatch('filter/resetAndSetFilters', filtersFromRouterQuery)
    updateSelectedFilterTagMap(selectedFilterTagMap)
    filterOptions.value = filtersOptionsRes

    // Drop any query params that are not valid for the current subcategory's filterOptions.
    // Use replace: true so this does NOT create an extra history entry — the subcategory
    // switch itself (handleSubcategorySelected) used router.push to create the entry, so we
    // only need to clean the current entry's query params.
    const validFilterKeys = new Set(Object.keys(filtersFromRouterQuery))
    const hasDroppedKeys = Object.keys(query).some(k => k !== 'page' && !validFilterKeys.has(k))
    if (hasDroppedKeys) {
      await nuxtApp.runWithContext(() => navigateTo({ query: filtersFromRouterQuery }, { replace: true }))
    }

    await fetchCategoryItemsByCardMode({
      sortby: sortType.value,
      filter_regionid: regionID.value,
      categoryId: firstLevelCategoryId.value,
      ...(isDataFromSubcategory && { sub_category_id: subcategoryId.value }),
      ...filtersFromRouterQuery,
      ...paginationParamOfList.value,
    })
  } catch (err) {
    console.error('useAsyncData error', err)
    throw showError({ statusCode: 404, message: 'Post not found', fatal: true })
  }
})

watch(loggedInUser, (newUser, oldUser) => {
  if (newUser && !oldUser) {
    pushGiftExpiringNotification(newUser)
  }
})

// Sync filter store and re-fetch when only the query string changes (e.g. browser back/forward)
useRouteQueryFilterSync({
  filterOptions,
  hasGeolocation,
  selectedFilters,
  pagingState,
  updateSelectedFilters,
  getProductList,
  onBeforeSync(newQuery) {
    const selectedFilterTagMap = getCategorySelectedFilterTagMapFromQueryString(newQuery.filter_tags, filterOptions.value?.filter_tags)
    updateSelectedFilterTagMap(selectedFilterTagMap)
  },
})

function handleSubcategorySelected(selectedCategoryInfo) {
  const { page, source_category_id, filter_keyword, ...otherFilterQuery } = route.query
  const isSelectedAllTab = selectedCategoryInfo.id === firstLevelCategoryId.value
  const pathName = selectedCategoryInfo.type === CategoryType.Theme ? 'regions-rid-themes-id' : 'regions-rid-categories-id'

  // reset keyword only, keep filters when switching subcategory
  store.dispatch('search/setSearchKeyword', '')

  loadingList.value = true
  const params = { rid: regionID.value, id: selectedCategoryInfo.id }
  router.push(
    nuxtApp.$localePath({
      name: pathName,
      params,
      query: {
        ...otherFilterQuery,
        ...(isSelectedAllTab ? {} : { source_category_id: firstLevelCategoryId.value }),
      },
    })
  )
}

function updateSelectedFilterTagMap(newFilterTagMap) {
  store.dispatch('filter/setFilterTags', newFilterTagMap)
}

function updateSelectedFilters(newPartialFilters) {
  store.dispatch('filter/setFilters', newPartialFilters)
}

function setNotification() {
  if (loggedInUser.value) {
    pushGiftExpiringNotification(loggedInUser.value)
  }
}

function onConditionUpdate({ sort, filter }) {
  const currentPage = pagingState.page
  const firstPage = 1
  // Preserve source_category_id in the query string when applying filters in subcategory mode.
  // filter may come from a partial source (e.g. search bar) that doesn't include source_category_id,
  // so we always forward it from the current route query to avoid dropping it.
  const sourceCategoryId = route.query.source_category_id
  const newQuery = {
    ...filter,
    page: firstPage,
    ...(sourceCategoryId ? { source_category_id: sourceCategoryId } : {}),
  }

  updateSelectedFilters(newQuery)
  updatePagingPage(firstPage)
  navigateTo({ query: newQuery })

  if (currentPage === firstPage) {
    getProductList()
  }
}

function getListPayloadWithoutFilterOptions() {
  let data = {
    sortby: sortType.value,
    filter_regionid: regionID.value,
    category_id: firstLevelCategoryId.value,
    ...paginationParamOfList.value,
    ...(hasGeolocation.value && { lng: userPosition.lng, lat: userPosition.lat }),
  }

  if (isUrlSubcategoryPattern.value && isSearchKeywordEmpty.value) {
    data = {
      ...data,
      sub_category_id: subcategoryId.value,
    }
  }

  if (searchKeyWord.value) {
    data.filter_keyword = searchKeyWord.value
  }

  return data
}

async function getProductList() {
  const payloadWithoutFilterOptions = getListPayloadWithoutFilterOptions()

  try {
    loadingList.value = true

    const { page, ...payload } = {
      ...payloadWithoutFilterOptions,
      ...selectedFilters.value,
    }
    await fetchCategoryItemsByCardMode(payload)
  } catch (error) {
    console.error(error)
  } finally {
    loadingList.value = false
  }
}

async function fetchCategoryItemsByCardMode(params) {
  const dispatchAction = isBranchCardMode.value ? 'branch/getBranchList' : 'product/getProductList'
  await nuxtApp.$store.dispatch(dispatchAction, params)
  sendGTMViewItemList()
}

async function fetchFilterOptions(params) {
  try {
    const filtersRes = await store.dispatch('filter/getFilters', params)

    return filtersRes.code === 0 ? filtersRes.data : {}
  } catch (error) {
    console.error('fetchFilterOptions: error', error)

    return {}
  }
}
function updatePagingPage(newPage) {
  pagingState.page = newPage
}
</script>

<style scoped lang="scss" src="@/assets/sass/pages/categories.scss"></style>
